import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Input, Modal } from 'ncoded-component-library';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import api from 'api';
import SearchIcon from 'icons/Search.icon';
import InfiniteScroll from 'components/InfiniteScroll';
import { InfiniteScrollRef } from 'components/InfiniteScroll/InfiniteScroll.component';
import SegmentedToggle from 'components/SegmentedToggle';
import useCallbackRef from 'hooks/useCallbackRef';
import useCloseOnEscape from 'hooks/useCloseOnEscape';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { ChatSessionFile } from 'models/Message';
import { PaginatedData } from 'models/PaginatedData';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import ChatFilesContext from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/providers/ChatFiles/ChatFiles.context';
import MessagingContext from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/providers/Messaging/Messaging.context';
import { getArrayLength, getIsArrayEmpty } from 'utils';
import ChatFileRow from '../ChatFileRow';
import ChatFileRowPlaceholder from '../ChatFileRowPlaceholder';
import MessageFilePreviewModal from '../MessageFilePreviewModal';

import './ChatFilesModal.styles.scss';

// the "All" bucket sends no `isDataRoomFile` at all, so it needs a value of its own
const ALL_FILES = 'all';

const DEFAULT_PARAMS = { $limit: '20', $sort: '-createdAt' };

// what a page requested for another chat or another bucket is worth here:
// `useInfinitePagination` merges whatever a request hands back, so it is dropped instead
const EMPTY_FILES_PAGE: PaginatedData<ChatSessionFile> = {
  items: [],
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
};

// keep the list loading ahead of the preview arrows so navigating never stalls on a request
const FILES_LOOKAHEAD = 5;

// past this the list scrolls inside itself, and its height is pinned so that filtering or
// searching never resizes the modal around it
const MAX_VISIBLE_ROWS = 5;

type ChatFilesModalProps = {
  className?: string;
  hasDataRoomAccess?: boolean;
  dataRoomId: string;
  dataRoomName: string;
  ownerId?: string;
  userSide?: QAndASides;
};

const ChatFilesModal: ForwardRefRenderFunction<
  OverlayRef,
  ChatFilesModalProps
> = (props, ref) => {
  const {
    className,
    hasDataRoomAccess,
    dataRoomId,
    dataRoomName,
    ownerId,
    userSide,
  } = props;

  const { t } = useTranslation();

  const { sessionId } = useContext(MessagingContext);
  const { totalFiles: totalSessionFiles, onFilePreview } =
    useContext(ChatFilesContext);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const [previewModal, previewModalRef] = useCallbackRef<OverlayRef>();
  const [listContainer, listContainerRef] = useCallbackRef<InfiniteScrollRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const [isDataRoomFile, setIsDataRoomFile] = useState<
    boolean | typeof ALL_FILES
  >(ALL_FILES);
  const [activeFileId, setActiveFileId] = useState<string>();
  const [visibleRows, setVisibleRows] = useState(0);
  const [bucketCounts, setBucketCounts] = useState<{
    saved: number;
    notSaved: number;
  }>();

  const isOpen = !!modal?.openState;

  // the library closes its own layers on Escape, but every closed `Dropdown` that
  // mounts pops the top of its stack - one row's menu is enough to unregister this
  // modal - so it takes the press through the app's own escape layers instead
  useCloseOnEscape(modal?.close, {
    enabled: isOpen,
    level: 'overlay',
    element: modal?.container,
  });

  // which chat and which bucket a response belongs to is decided when it lands, not when
  // it was requested
  const requestedFor = useRef<string>();

  useEffect(() => {
    requestedFor.current = `${sessionId}|${isDataRoomFile}`;
  }, [sessionId, isDataRoomFile]);

  const {
    items: files,
    loading,
    searchString,
    setItems: setFiles,
    setSearchString,
    setCurrentPage,
    shouldContinuePagination,
    onContainerScrolled,
  } = useInfinitePagination<ChatSessionFile>({
    condition: isOpen && !!sessionId,
    resetDeps: [sessionId, isDataRoomFile, isOpen],
    dependencies: [sessionId, isDataRoomFile],
    makeRequest: async (currentPage, searchString) => {
      const scope = `${sessionId}|${isDataRoomFile}`;

      const pagination = await api.messages
        .getMessagesFiles(sessionId, {
          $page: currentPage.toString(),
          ...DEFAULT_PARAMS,
          ...(searchString && { $searchString: searchString }),
          ...(isDataRoomFile !== ALL_FILES && { isDataRoomFile }),
        })
        .then((res) => res.data);

      // the chat or the bucket may have changed while this was in flight: the hook merges
      // what it is handed, so these files would end up under the wrong heading
      if (scope !== requestedFor.current) return EMPTY_FILES_PAGE;

      return pagination;
    },
  });

  const totalFiles = getArrayLength(files);

  const isEmpty = getIsArrayEmpty(files);

  const activeIndex = useMemo(
    () => files.findIndex(({ _id }) => _id === activeFileId),
    [files, activeFileId],
  );

  const activeFileName = files[activeIndex]?.file?.name;

  const openPreview = useCallback(
    (fileId: string) => {
      setActiveFileId(fileId);
      previewModal?.open();
    },
    [previewModal],
  );

  const onPrev = useCallback(() => {
    if (activeIndex === -1 || !totalFiles) return;

    setActiveFileId(
      files[activeIndex === 0 ? totalFiles - 1 : activeIndex - 1]._id,
    );
  }, [activeIndex, files, totalFiles]);

  const onNext = useCallback(() => {
    if (activeIndex === -1 || !totalFiles) return;

    setActiveFileId(
      files[activeIndex === totalFiles - 1 ? 0 : activeIndex + 1]._id,
    );
  }, [activeIndex, files, totalFiles]);

  useEffect(() => {
    if (activeIndex === -1 || activeIndex < totalFiles - FILES_LOOKAHEAD)
      return;
    if (loading || !shouldContinuePagination) return;

    onContainerScrolled();
  }, [
    activeIndex,
    totalFiles,
    loading,
    shouldContinuePagination,
    onContainerScrolled,
  ]);

  // The list, the filter and the search belong to one visit and one chat, so opening,
  // closing or switching chat starts them over. The hook's own reset can't be relied on
  // here: it is skipped whenever a request is in flight, and while the modal is closed
  // nothing ever finishes one — `loading` stays true and every reset is skipped. Left to
  // it, reopening in another chat lists the previous chat's files.
  useEffect(() => {
    setIsDataRoomFile(ALL_FILES);
    setSearchString('');
    setActiveFileId(undefined);
    setBucketCounts(undefined);
    setVisibleRows(0);
    setFiles([]);
    setCurrentPage(1);
  }, [isOpen, sessionId, setSearchString, setFiles, setCurrentPage]);

  // a new bucket is a new list, so it starts at the top rather than wherever the previous
  // one was scrolled to
  useEffect(() => {
    if (!listContainer?.container) return;

    listContainer.container.scrollTop = 0;
  }, [isDataRoomFile, listContainer]);

  const removeFromList = useCallback(
    (fileId: string) =>
      setFiles((old) => old.filter(({ _id }) => _id !== fileId)),
    [setFiles],
  );

  // a file that has just been saved no longer belongs in the "Not saved" bucket
  const onSavedToDataRoom = useCallback(
    (fileId: string) => {
      // it changes bucket rather than leaving the session, so the counts move with it
      // instead of waiting for the next fetch
      setBucketCounts((old) =>
        old
          ? { saved: old.saved + 1, notSaved: Math.max(old.notSaved - 1, 0) }
          : old,
      );

      if (isDataRoomFile === false) {
        removeFromList(fileId);
        return;
      }

      setFiles((old) =>
        old.map((file) =>
          file._id === fileId ? { ...file, isDataRoomFile: true } : file,
        ),
      );
    },
    [isDataRoomFile, removeFromList, setFiles],
  );

  // a bucket's count is its own `totalItems` — the route hands back no counts object — so
  // each status is counted with a throwaway page of its own, and "All" is their sum. The
  // counts describe the chat, not the search, which is what decides whether the filter is
  // shown at all. Adding or removing a file moves the session total, which refetches them.
  useEffect(() => {
    if (!isOpen || !sessionId) return;

    let cancelled = false;

    const countFiles = (isDataRoomFile: boolean) =>
      api.messages
        .getMessagesFiles(sessionId, { $limit: '1', isDataRoomFile })
        .then(({ data }) => data.totalItems);

    Promise.all([countFiles(true), countFiles(false)])
      .then(([saved, notSaved]) => {
        if (cancelled) return;

        setBucketCounts({ saved, notSaved });
      })
      .catch(() => {
        if (cancelled) return;

        setBucketCounts(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, sessionId, totalSessionFiles]);

  const savedCount = bucketCounts?.saved ?? 0;
  const notSavedCount = bucketCounts?.notSaved ?? 0;

  const segmentOptions = useMemo(
    () => [
      {
        label: `${t('all')} (${savedCount + notSavedCount})`,
        value: ALL_FILES,
      },
      {
        label: `${t('notSavedToDataRoom')} (${notSavedCount})`,
        value: false,
      },
      { label: `${t('inDataRoom')} (${savedCount})`, value: true },
    ],
    [savedCount, notSavedCount, t],
  );

  // without Data Room access there is no status to filter on, and a chat whose files are
  // all of one status has nothing to filter — every bucket with anything in it is already
  // on screen
  const showFilter = hasDataRoomAccess && !!savedCount && !!notSavedCount;

  // the last file of a status takes the filter away with it — without this the list would
  // be stranded on a bucket that can no longer be switched off
  useEffect(() => {
    if (showFilter) return;

    setIsDataRoomFile(ALL_FILES);
  }, [showFilter]);

  const emptyText = useMemo(() => {
    if (searchString) return t('noFilesMatchSearch');

    return isDataRoomFile === ALL_FILES
      ? t('noFilesYet')
      : t('noFilesInThisView');
  }, [searchString, isDataRoomFile, t]);

  useEffect(() => {
    if (loading) return;

    setVisibleRows(totalFiles);
  }, [loading, totalFiles]);

  // switching bucket or typing empties the list while the next one loads: rather than
  // collapsing the modal around a loader, it keeps the rows it had as placeholders. The
  // session total stands in on the first open, when there is nothing to keep.
  const showPlaceholders = loading && isEmpty;

  const placeholderRows = Math.min(
    visibleRows || totalSessionFiles || 1,
    MAX_VISIBLE_ROWS,
  );

  const classes = classNames('chat-files-modal', className);

  const listClasses = classNames('chat-files-modal__list', {
    'chat-files-modal__list--pinned': totalSessionFiles >= MAX_VISIBLE_ROWS,
    'chat-files-modal__list--loading': showPlaceholders,
  });

  return (
    <>
      <Modal ref={modalRef} className={classes} title={t('filesInChat')}>
        <p className="chat-files-modal__description">
          {hasDataRoomAccess
            ? t('filesInChatDescription')
            : t('filesInChatDescriptionNoDataRoom')}
        </p>

        <div className="chat-files-modal__controls">
          {showFilter && (
            <SegmentedToggle
              className="chat-files-modal__filter"
              options={segmentOptions}
              selectedValue={isDataRoomFile}
              onChange={(value) =>
                setIsDataRoomFile(value as boolean | typeof ALL_FILES)
              }
            />
          )}

          <Input
            className="chat-files-modal__search"
            value={searchString}
            prefixNode={<SearchIcon />}
            placeholder={t('searchFiles')}
            onChange={(e) => setSearchString(e.target.value)}
          />
        </div>

        {showPlaceholders ? (
          <div className={listClasses}>
            <ul>
              {Array.from({ length: placeholderRows }).map((_, index) => (
                <ChatFileRowPlaceholder
                  key={index}
                  withStatus={hasDataRoomAccess}
                />
              ))}
            </ul>
          </div>
        ) : isEmpty ? (
          <div className="chat-files-modal__empty">
            <p>{emptyText}</p>
          </div>
        ) : (
          <InfiniteScroll
            localScroll
            ref={listContainerRef}
            className={listClasses}
            onScroll={onContainerScrolled}
          >
            <ul>
              {files.map((chatSessionFile) => (
                <ChatFileRow
                  key={chatSessionFile._id}
                  variant="detailed"
                  chatSessionFile={chatSessionFile}
                  hasDataRoomAccess={hasDataRoomAccess}
                  dataRoomId={dataRoomId}
                  dataRoomName={dataRoomName}
                  ownerId={ownerId}
                  userSide={userSide}
                  onOpen={openPreview}
                  onRemoved={removeFromList}
                  onSavedToDataRoom={onSavedToDataRoom}
                />
              ))}

              {/* the next page loads inside the scroll box, so it never resizes the modal */}
              {loading && shouldContinuePagination && (
                <ChatFileRowPlaceholder withStatus={hasDataRoomAccess} />
              )}
            </ul>
          </InfiniteScroll>
        )}
      </Modal>

      <MessageFilePreviewModal
        ref={previewModalRef}
        fileId={activeFileId}
        fileName={activeFileName}
        scope={sessionId}
        onFilePreview={onFilePreview}
        {...(totalFiles > 1 && activeIndex !== -1 && { onPrev, onNext })}
      />
    </>
  );
};

export default forwardRef(ChatFilesModal);
